import i18n from "i18next";
import { getDealSuccess } from "src/store/actions/deal";
import { pollingSuccess } from "src/store/actions/poller";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { TradeIn, Yn } from "Types/TradeIn";
import {
    deleteTradeInFailure,
    deleteTradeInRequest,
    deleteTradeInSuccess,
    getTradePendingDetailsFailure,
    getTradePendingDetailsRequest,
    getTradePendingDetailsSuccess,
    getAndPatchTradePendingReportFailure,
    getAndPatchTradePendingReportRequest,
    getAndPatchTradePendingReportSuccess,
    patchTradeInFailure,
    patchTradeInRequest,
    patchTradeInSuccess,
    toggleTradeInEstimateModeTradePending,
    toggleTradeInEstimateModeManual,
    toggleTradeInVehicleModeFallback,
    toggleTradeInVehicleModeDefault,
} from "../actions/tradeIn";

type AnyStatusActions =
    | ActionType<typeof patchTradeInRequest>
    | ActionType<typeof deleteTradeInRequest>
    | ActionType<typeof getTradePendingDetailsRequest>
    | ActionType<typeof getAndPatchTradePendingReportRequest>;
type IdleStatusActions =
    | ActionType<typeof toggleTradeInEstimateModeManual>
    | ActionType<typeof toggleTradeInEstimateModeTradePending>
    | ActionType<typeof toggleTradeInVehicleModeDefault>
    | ActionType<typeof toggleTradeInVehicleModeFallback>
    | ActionType<typeof getDealSuccess>
    | ActionType<typeof pollingSuccess>;
type LoadingStatusActions =
    | ActionType<typeof getTradePendingDetailsSuccess>
    | ActionType<typeof getTradePendingDetailsFailure>
    | ActionType<typeof getAndPatchTradePendingReportSuccess>
    | ActionType<typeof getAndPatchTradePendingReportFailure>;
type UpdatingStatusActions = ActionType<typeof patchTradeInSuccess> | ActionType<typeof patchTradeInFailure>;
type DeletingStatusActions = ActionType<typeof deleteTradeInSuccess> | ActionType<typeof deleteTradeInFailure>;

export enum Status {
    Idle = "idle",
    Loading = "loading",
    Updating = "updating",
    Deleting = "deleting",
}

export enum EstimateMode {
    Manual = "manual",
    TradePending = "trade_pending",
}

export enum VehicleInputMode {
    Default = "typeahead",
    Fallback = "manual_input",
}

export interface TradeInState extends TradeIn {
    estimateMode: EstimateMode;
    vehicleInputMode: VehicleInputMode;
    status: Status;
    choices: string[];
    question: "body" | "doors" | "drivetrain" | "engine" | "fuel_type" | null;
    answers: {
        body?: string;
        doors?: string;
        drivetrain?: string;
        engine?: string;
        fuel_type?: string;
    };
    requests: number;
    errorMessage?: string;
    value?:
        | {
              high: number | null;
              low: number | null;
              target: number | null;
          }
        | undefined;
}

const initialState: TradeInState = {
    status: Status.Idle,
    yn: Yn.No,
    estimateMode: EstimateMode.TradePending,
    vehicleInputMode: VehicleInputMode.Default,
    choices: [],
    question: null,
    answers: {},
    requests: 0,
};

const anyStatusReducer = (state: TradeInState, action: AnyStatusActions) => {
    switch (action.type) {
        case getTradePendingDetailsRequest.type:
            if (action.payload.shouldPatch) {
                return {
                    ...state,
                    year: action.payload.details.year,
                    make: action.payload.details.make,
                    model: action.payload.details.model,
                    trim: action.payload.details.trim,
                    ymmt: action.payload.details.ymmt,
                    answers: action.payload.details.answers,
                    choices: [],
                    question: null,
                    status: Status.Loading,
                    requests: state.requests + 1,
                };
            }

            return {
                ...state,
                answers: action.payload.details.answers,
                choices: [],
                question: null,
                status: Status.Loading,
                requests: state.requests + 1,
            };

        case getAndPatchTradePendingReportRequest.type:
            return { ...state, status: Status.Loading, requests: state.requests + 1 };

        case patchTradeInRequest.type:
            return {
                ...state,
                ...action.payload.tradeIn,
                status: state.requests === 0 ? Status.Updating : state.status,
                requests: state.requests + 1,
            };

        case deleteTradeInRequest.type:
            return { ...initialState, status: Status.Deleting, requests: state.requests + 1 };

        default:
            return state;
    }
};

const idleStatusReducer = (state: TradeInState, action: IdleStatusActions | AnyStatusActions) => {
    switch (action.type) {
        case getDealSuccess.type:
        case pollingSuccess.type:
            return { ...state, ...action.payload.deal.tradein, status: Status.Idle };

        case toggleTradeInEstimateModeManual.type:
            return {
                ...state,
                estimateMode: EstimateMode.Manual,
            };

        case toggleTradeInEstimateModeTradePending.type:
            return {
                ...state,
                estimateMode: EstimateMode.TradePending,
            };

        case toggleTradeInVehicleModeFallback.type:
            return {
                ...state,
                vehicleInputMode: VehicleInputMode.Fallback,
            };

        case toggleTradeInVehicleModeDefault.type:
            return {
                ...state,
                vehicleInputMode: VehicleInputMode.Default,
            };

        default:
            return anyStatusReducer(state, action);
    }
};

const busyStatusReducer = (
    state: TradeInState,
    action: LoadingStatusActions | UpdatingStatusActions | DeletingStatusActions | AnyStatusActions
) => {
    const finishRequest = () => ({
        status: state.requests === 1 ? Status.Idle : state.status,
        requests: state.requests > 0 ? state.requests - 1 : 0,
    });

    switch (action.type) {
        case patchTradeInSuccess.type:
            return {
                ...state,
                ...finishRequest(),
            };

        case patchTradeInFailure.type:
            return {
                ...state,
                ...finishRequest(),
            };

        case deleteTradeInSuccess.type:
            return { ...initialState };

        case deleteTradeInFailure.type:
            return {
                ...initialState,
                ...finishRequest(),
            };

        case getTradePendingDetailsSuccess.type:
            return {
                ...state,
                choices: action.payload.choices,
                question: action.payload.question,
                id: action.payload.id,
                ...finishRequest(),
            };

        case getTradePendingDetailsFailure.type:
            return {
                ...state,
                estimateMode: EstimateMode.Manual,
                ...finishRequest(),
            };

        case getAndPatchTradePendingReportSuccess.type:
            return {
                ...state,
                ...action.payload.tradeIn,
                errorMessage: undefined,
                ...finishRequest(),
            };

        case getAndPatchTradePendingReportFailure.type:
            return {
                ...state,
                errorMessage:
                    action.payload.error === "NotEnoughDataError" // Report request came back without an estimate
                        ? i18n.t("TradePending.notEnoughDataMessage")
                        : action.payload.error === "MissingTradePendingIdError" // CCDP gave us a bad TradeIn, no error message needed
                        ? undefined
                        : i18n.t("TradePending.genericNoEstimatedValuesMessage"), // Fallback to a generic message for any other error
                ...finishRequest(),
            };

        default:
            return anyStatusReducer(state, action);
    }
};

const tradeInReducer = (
    state = initialState,
    action: AnyStatusActions | IdleStatusActions | LoadingStatusActions | UpdatingStatusActions | DeletingStatusActions
): TradeInState => {
    switch (state.status) {
        case Status.Idle:
            return idleStatusReducer(state, action as IdleStatusActions | AnyStatusActions);
        case Status.Updating:
        case Status.Loading:
        case Status.Deleting:
            return busyStatusReducer(
                state,
                action as LoadingStatusActions | UpdatingStatusActions | DeletingStatusActions | AnyStatusActions
            );
    }
};

export default tradeInReducer;
